import React, { useEffect, useRef, useState } from "react";
import { Loader2, Music, Save, Sparkles, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/Card";
import { eventsApi, settingsApi } from "../../utils/api";
import { capitalize } from "../../utils/helpers";
import { formatArtistData, searchArtists } from "../../utils/musicBrainzApi";

// Curated onboarding suggestions live in the app_settings table and are served to
// the consumer onboarding steps. Artists are added by searching MusicBrainz (the
// same source the consumer onboarding search uses); themes by searching the
// canonical themes index. Saving replaces the lists wholesale.
//
// Note: saving does NOT re-index Elasticsearch. To add a brand-new suggested artist
// with full enrichment, extend scripts/seeds/seed_onboarding_suggestions.py (in the API)
// and re-run it; rows added here that don't yet exist in ES are resolved/created
// when a user first adds them during onboarding.

function Banner({ message, error }) {
  if (!message && !error) return null;
  const cls = error
    ? "bg-red-500/10 text-red-700 border-red-500/20"
    : "bg-green-500/10 text-green-700 border-green-500/20";
  return <div className={`mb-4 p-3 rounded-md text-sm border ${cls}`}>{error || message}</div>;
}

/**
 * Debounced search box with a results dropdown, mirroring the consumer onboarding
 * search UX. `search(query)` must resolve to an array of
 * `{ key, label, sublabel?, value }`. Picking calls `onSelect(value)`. When
 * `onCreate` is provided, a "use <query>" row lets the admin add free text.
 */
function SearchAdd({ placeholder, search, onSelect, onCreate }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(search);
  searchRef.current = search;

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await searchRef.current(q);
        if (!cancelled) setResults(Array.isArray(r) ? r : []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query]);

  const reset = () => {
    setQuery("");
    setResults([]);
    setLoading(false);
  };
  const pick = (value) => {
    onSelect(value);
    reset();
  };

  const q = query.trim();
  const showDropdown = q.length >= 2;

  return (
    <div className="relative">
      <Input placeholder={placeholder} value={query} onChange={(e) => setQuery(e.target.value)} />
      {showDropdown && (
        <div className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded-md border border-border bg-background shadow-lg">
          {loading && (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />Searching...
            </div>
          )}
          {!loading &&
            results.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => pick(r.value)}
                className="block w-full text-left px-3 py-2 text-sm hover:bg-accent"
              >
                <span className="font-medium">{r.label}</span>
                {r.sublabel ? <span className="text-muted-foreground"> · {r.sublabel}</span> : null}
              </button>
            ))}
          {!loading && results.length === 0 && !onCreate && (
            <div className="px-3 py-2 text-sm text-muted-foreground">No matches.</div>
          )}
          {!loading && onCreate && (
            <button
              type="button"
              onClick={() => {
                onCreate(q);
                reset();
              }}
              className="block w-full text-left px-3 py-2 text-sm hover:bg-accent border-t border-border"
            >
              Use “{q}”
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Read-only summary of the MusicBrainz-derived fields for a stored artist.
function ResolvedArtistInfo({ artist }) {
  if (!artist.name) {
    return <span className="text-sm text-muted-foreground italic">resolves from MusicBrainz on save</span>;
  }
  const tags = (artist.tags || []).slice(0, 3).join(", ");
  return (
    <span className="text-sm">
      <span className="font-medium">{artist.name}</span>
      {artist.country ? <span className="text-muted-foreground"> · {artist.country}</span> : null}
      {tags ? <span className="text-muted-foreground"> · {tags}</span> : null}
    </span>
  );
}

function ArtistRows({ artists, onChange, disabled = false }) {
  const remove = (idx) => onChange(artists.filter((_, i) => i !== idx));
  const addArtist = (artist) => {
    if (!artist?.mbid || artists.some((a) => a.mbid === artist.mbid)) return;
    onChange([...artists, artist]);
  };

  const searchArtistOptions = async (q) =>
    (await searchArtists(q, 8)).map((a) => ({
      key: a.id,
      label: a.name,
      sublabel: [a.country, a.disambiguation && `(${a.disambiguation})`].filter(Boolean).join(" · "),
      value: formatArtistData(a),
    }));

  return (
    <div className="space-y-2">
      {artists.length === 0 && (
        <p className="text-sm text-muted-foreground">{disabled ? "No suggestions stored." : "No artists yet."}</p>
      )}
      {artists.map((a, idx) => (
        <div key={a.mbid || idx} className="flex items-center gap-2">
          <div className="min-w-0 flex-1 truncate">
            <ResolvedArtistInfo artist={a} />
          </div>
          {!disabled && (
            <button
              type="button"
              aria-label="Remove artist"
              onClick={() => remove(idx)}
              className="flex items-center justify-center text-muted-foreground hover:text-red-600"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}
      {!disabled && (
        <SearchAdd placeholder="Search MusicBrainz to add an artist…" search={searchArtistOptions} onSelect={addArtist} />
      )}
    </div>
  );
}

export default function OnboardingSuggestions() {
  const [artists, setArtists] = useState(null); // { base: [], by_city: {} }
  const [themes, setThemes] = useState(null); // [{ id, name }]
  const [loading, setLoading] = useState(true);
  const [savingArtists, setSavingArtists] = useState(false);
  const [savingThemes, setSavingThemes] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  // Cities are taken from the configured cities in Postgres, not free-form input,
  // so admins can only curate suggestions for cities that actually exist.
  const [cities, setCities] = useState([]);

  useEffect(() => {
    let active = true;
    Promise.all([
      settingsApi.getSuggestedArtists(),
      settingsApi.getSuggestedThemes(),
      eventsApi.getScanCities().catch(() => []),
    ])
      .then(([artistsData, themesData, citiesData]) => {
        if (!active) return;
        setArtists({ base: artistsData.base || [], by_city: artistsData.by_city || {} });
        setThemes(themesData.themes || []);
        setCities(Array.isArray(citiesData) ? citiesData : []);
      })
      .catch(() => active && setError("Failed to load onboarding suggestions"))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(t);
  }, [message]);

  const setBase = (base) => setArtists((prev) => ({ ...prev, base }));
  const setCityArtists = (city, list) =>
    setArtists((prev) => ({ ...prev, by_city: { ...prev.by_city, [city]: list } }));

  const configuredKeys = new Set(cities.map((c) => String(c).toLowerCase()));
  // Cities that have stored suggestions but aren't currently configured in Postgres.
  // Expected to be rare (cities are not removed); shown read-only so the data stays
  // visible and is preserved on save rather than silently dropped.
  const orphanCities = artists
    ? Object.keys(artists.by_city).filter((key) => !configuredKeys.has(key))
    : [];

  // Send the MBID plus any already-resolved fields. The backend fills name/country/
  // tags for newly-picked entries (name but no tags yet). Rows without an MBID are dropped.
  const cleanArtist = (a) => ({
    mbid: (a.mbid || "").trim(),
    name: (a.name || "").trim() || null,
    country: a.country ?? null,
    tags: a.tags || [],
  });

  const saveArtists = async () => {
    setSavingArtists(true);
    setError(null);
    setMessage(null);
    try {
      // Persist per-city lists for configured cities (keyed lowercase, as the
      // consumer endpoint looks them up); cities with no artists are omitted.
      const byCity = {};
      for (const city of cities) {
        const key = String(city).toLowerCase();
        const list = (artists.by_city[key] || []).map(cleanArtist).filter((a) => a.mbid);
        if (list.length) byCity[key] = list;
      }
      // Preserve orphaned cities untouched — they are read-only here.
      for (const key of orphanCities) {
        byCity[key] = artists.by_city[key];
      }
      const payload = {
        base: artists.base.map(cleanArtist).filter((a) => a.mbid),
        by_city: byCity,
      };
      const updated = await settingsApi.updateSuggestedArtists(payload);
      setArtists({ base: updated.base || [], by_city: updated.by_city || {} });
      setMessage("Suggested artists updated");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update suggested artists");
    } finally {
      setSavingArtists(false);
    }
  };

  const addTheme = (theme) => {
    const name = (theme?.name || "").trim();
    if (!name) return;
    const key = (theme.id || name).toLowerCase();
    if (themes.some((t) => (t.id || t.name || "").toLowerCase() === key)) return;
    setThemes([...themes, { id: theme.id || null, name }]);
  };

  const searchThemeOptions = async (q) =>
    (await settingsApi.searchThemes(q, 8)).map((t) => ({
      key: t.id,
      label: t.name,
      sublabel: t.cluster_id || null,
      value: { id: t.id, name: t.name },
    }));

  const saveThemes = async () => {
    setSavingThemes(true);
    setError(null);
    setMessage(null);
    try {
      const payload = {
        themes: themes
          .map((t) => ({ id: (t.id || "").trim() || null, name: (t.name || "").trim() }))
          .filter((t) => t.name),
      };
      const updated = await settingsApi.updateSuggestedThemes(payload);
      setThemes(updated.themes || []);
      setMessage("Suggested themes updated");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update suggested themes");
    } finally {
      setSavingThemes(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />Loading suggestions...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Music className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Suggested Artists</CardTitle>
          </div>
          <CardDescription>
            Curated artists shown on the onboarding Artists step. Search MusicBrainz and pick an artist
            to add it — the name, country, and genres are filled in automatically on save. Local-city
            acts are listed before the base list; the per-city sections come from the configured cities
            in Postgres. Saving does not re-index Elasticsearch — to add a brand-new artist with full
            enrichment, extend and re-run the API seeding script.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Banner message={message} error={error} />
          {artists ? (
            <div className="space-y-6">
              <div>
                <p className="text-sm font-medium mb-2">Base (all cities)</p>
                <ArtistRows artists={artists.base} onChange={setBase} />
              </div>

              {cities.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No configured cities found. Add cities in Postgres (city_search_areas) to curate
                  local-act suggestions per city.
                </p>
              ) : (
                cities.map((city) => {
                  const key = String(city).toLowerCase();
                  const list = artists.by_city[key] || [];
                  return (
                    <div key={key}>
                      <p className="text-sm font-medium mb-2">{capitalize(city)}</p>
                      <ArtistRows artists={list} onChange={(l) => setCityArtists(key, l)} />
                    </div>
                  );
                })
              )}

              {orphanCities.map((key) => (
                <div key={key} className="opacity-60">
                  <p className="text-sm font-medium mb-1">{capitalize(key)}</p>
                  <p className="text-xs text-muted-foreground mb-2">
                    Not a currently configured city in Postgres — shown read-only. Re-add it in
                    city_search_areas to edit these suggestions again.
                  </p>
                  <ArtistRows artists={artists.by_city[key] || []} onChange={() => {}} disabled />
                </div>
              ))}

              <Button type="button" onClick={saveArtists} disabled={savingArtists}>
                {savingArtists ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                ) : (
                  <><Save className="mr-2 h-4 w-4" />Save artists</>
                )}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Suggested Themes</CardTitle>
          </div>
          <CardDescription>
            Curated themes shown on the onboarding Themes step. Search existing canonical themes and
            pick one, or use a new name (created on first use during onboarding).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {themes ? (
            <div className="space-y-2">
              {themes.length === 0 && <p className="text-sm text-muted-foreground">No themes yet.</p>}
              {themes.map((t, idx) => (
                <div key={t.id || t.name || idx} className="flex items-center gap-2">
                  <div className="flex-1 min-w-0 text-sm truncate">
                    <span className="font-medium">{t.name}</span>
                    {t.id ? <span className="text-muted-foreground"> · {t.id}</span> : null}
                  </div>
                  <button
                    type="button"
                    aria-label="Remove theme"
                    onClick={() => setThemes(themes.filter((_, i) => i !== idx))}
                    className="flex items-center justify-center text-muted-foreground hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <SearchAdd
                placeholder="Search themes to add…"
                search={searchThemeOptions}
                onSelect={addTheme}
                onCreate={(name) => addTheme({ id: null, name })}
              />
              <div className="pt-1">
                <Button type="button" onClick={saveThemes} disabled={savingThemes}>
                  {savingThemes ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                  ) : (
                    <><Save className="mr-2 h-4 w-4" />Save themes</>
                  )}
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </>
  );
}
